import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { logError } from '../lib/logger';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO, { faqJsonLd } from '../components/SEO';

const rmaFaqs = [
  {
    question: 'How often does the CTRL UL101 need calibration?',
    answer:
      'The CTRL UL101 ultrasonic sensor requires biennial calibration (every two years) to maintain the product\'s warranty. Our factory-certified service ensures your device meets all manufacturer specifications and regulatory requirements.',
  },
  {
    question: 'What is the calibration turnaround time?',
    answer:
      'CTRL UL101 calibration typically has a 10–15 business day turnaround time and includes OEM-authorized calibration certificate and full sensitivity validation.',
  },
  {
    question: 'Do FOTRIC acoustic imaging cameras need calibration?',
    answer:
      'Yes, FOTRIC H-Series acoustic imaging cameras require periodic calibration for optimal acoustic detection accuracy. Our authorized service center provides factory-authorized calibration, acoustic sensor array validation, and software updates.',
  },
  {
    question: 'What is an RMA and when do I need one?',
    answer:
      'A Return Merchandise Authorization (RMA) is required whenever a CTRL or FOTRIC product needs service, repair, or warranty replacement. Our team reviews each request and provides authorization along with return shipping instructions.',
  },
  {
    question: 'What should I do before submitting an RMA?',
    answer:
      'Before submitting an RMA: locate your product serial number, review warranty coverage on the Warranty page, contact technical support if troubleshooting is needed, and back up any data or configurations from your device.',
  },
];

const CalibrationRMAPage = ({ onRequestDemo, onRequestQuote }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    productBrand: '',
    productModel: '',
    serialNumber: '',
    issueDescription: '',
    serviceType: 'rma'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        form_type: formData.serviceType === 'calibration' ? 'Calibration Request' : 'RMA Request',
        name: formData.name,
        email: formData.email,
        company: formData.company,
        company_description: 'Other',
        phone: formData.phone,
        product: 'UL101',
        message: [
          `Brand: ${formData.productBrand}`,
          `Model: ${formData.productModel}`,
          `Serial: ${formData.serialNumber}`,
          `Issue: ${formData.issueDescription}`,
        ].join('\n'),
      });
      if (!data.success) throw new Error(data.message || 'Submission failed');
    } catch (err) {
      logError('RMA request failed', err);
      toast.error('We could not submit your request. Please try again or email support@ctrlsys.com.');
      return;
    }
    toast.success('Thank you! Your RMA request has been submitted. We will contact you shortly.');
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      productBrand: '',
      productModel: '',
      serialNumber: '',
      issueDescription: '',
      serviceType: 'rma'
    });
    setShowForm(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Calibration & RMA — ctrl.aero"
        description="Request calibration service or an RMA for your CTRL Systems ultrasonic troubleshooter or FOTRIC acoustic imager."
        jsonLd={faqJsonLd(rmaFaqs)}
      />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/images/img_0235.jpeg"
            alt="Aviation"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Calibration & RMA Services
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Professional calibration services and return merchandise authorization for all CTRL and FOTRIC products
          </p>
        </div>
      </section>

      {/* Calibration Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-[#1e2d3b] mb-8 text-center">
              Calibration Services
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* CTRL Calibration */}
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 border-2 border-slate-100">
                <div className="flex items-center mb-6">
                  <img 
                    src="/images/ctrl-bar-logo-no-border-.png"
                    alt="CTRL Systems"
                    className="h-10 w-auto"
                  />
                </div>
                <h3 className="text-2xl font-bold text-[#1e2d3b] mb-4">CTRL UL101 Calibration</h3>
                <p className="text-slate-700 leading-relaxed mb-6">
                  The CTRL UL101 ultrasonic sensor requires <strong>biennial calibration to maintain the product&rsquo;s warranty</strong>. Our factory-certified calibration service ensures your device meets all manufacturer specifications and regulatory requirements.
                </p>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>OEM-authorized calibration certificate</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>10-15 business day turnaround time</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Full sensitivity validation included</span>
                  </li>
                </ul>
              </div>

              {/* FOTRIC Calibration */}
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 border-2 border-slate-100">
                <div className="flex items-center mb-6">
                  <img 
                    src="/images/fotric-logo-transparent.png"
                    alt="FOTRIC"
                    className="h-10 w-auto"
                  />
                </div>
                <h3 className="text-2xl font-bold text-[#1e2d3b] mb-4">FOTRIC Acoustic Camera Calibration</h3>
                <p className="text-slate-700 leading-relaxed mb-6">
                  FOTRIC H-Series acoustic imaging cameras require periodic calibration for optimal acoustic detection accuracy. Our authorized service center provides comprehensive calibration and sensor validation.
                </p>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Factory-authorized calibration</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Acoustic sensor array validation</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Software updates included</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* RMA Process */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-[#1e2d3b] mb-8 text-center">
              Return Merchandise Authorization (RMA)
            </h2>
            <div className="bg-white rounded-2xl p-8 border-2 border-slate-200 max-w-4xl mx-auto">
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                If your CTRL or FOTRIC product requires service, repair, or warranty replacement, please submit an RMA request. Our team will review your request and provide authorization along with return shipping instructions.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mb-8">
                <h4 className="font-bold text-[#1e2d3b] mb-2">Before Submitting an RMA:</h4>
                <ul className="space-y-2 text-slate-700">
                  <li>• Ensure you have your product serial number</li>
                  <li>• Review warranty coverage on the Warranty page</li>
                  <li>• Contact technical support if troubleshooting is needed</li>
                  <li>• Backup any data or configurations from your device</li>
                </ul>
              </div>
              <div className="cta-group max-w-3xl mx-auto">
                <Button
                  onClick={() => setShowForm(true)}
                  data-testid="open-ctrl-rma-btn"
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 text-sm"
                >
                  Submit RMA Request for CTRL-manufactured Product
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-slate-300 hover:border-blue-500 text-[#1e2d3b] px-6 text-sm hover:bg-blue-50"
                >
                  <a
                    href="https://www.fotric.com/technical-support"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cal-fotric-support-link"
                  >
                    Contact FOTRIC Technical Support
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RMA Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-3xl font-bold text-[#1e2d3b]">
                  RMA Request Form
                </h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Company *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Service Type *
                  </label>
                  <select
                    required
                    value={formData.serviceType}
                    onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="rma">Warranty RMA</option>
                    <option value="repair">Out-of-Warranty Repair</option>
                    <option value="calibration">Calibration Service</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Product Brand *
                    </label>
                    <select
                      required
                      value={formData.productBrand}
                      onChange={(e) => setFormData({ ...formData, productBrand: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Brand</option>
                      <option value="ctrl">CTRL Systems</option>
                      <option value="fotric">FOTRIC</option>
                      <option value="dalcomm">Dalcomm Tech</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Product Model *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g., UL101, H6, etc."
                      value={formData.productModel}
                      onChange={(e) => setFormData({ ...formData, productModel: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Serial Number *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Located on product label"
                    value={formData.serialNumber}
                    onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Issue Description *
                  </label>
                  <textarea
                    required
                    value={formData.issueDescription}
                    onChange={(e) => setFormData({ ...formData, issueDescription: e.target.value })}
                    rows={5}
                    placeholder="Please describe the issue, error messages, or reason for return..."
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    variant="outline"
                    className="flex-1 border-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
                  >
                    Submit RMA Request
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CalibrationRMAPage;
